/**
 * Competitive comparison — distilled from the competitive-research findings.
 * Columns are categories, not single products, to stay accurate and defensible.
 */
const Comparison = () => {
  const columns = [
    { key: 'us', label: 'SolidlyAI Agents', sub: 'this app', highlight: true },
    { key: 'coding', label: 'Coding-agent GUIs', sub: 'Conductor, Crystal/Nimbalyst' },
    { key: 'notetaker', label: 'Meeting notetakers', sub: 'Granola, Otter, Meetily' },
    { key: 'cloud', label: 'Cloud agent suites', sub: 'Zoom, Otter Agents' },
  ];

  // 'yes' | 'no' | 'partial'
  const rows = [
    { feature: 'Scheduled + coordinating agent team', us: 'yes', coding: 'no', notetaker: 'no', cloud: 'partial' },
    { feature: 'Agent-to-agent handoffs', us: 'yes', coding: 'no', notetaker: 'no', cloud: 'partial' },
    { feature: 'Live meeting transcription', us: 'yes', coding: 'no', notetaker: 'yes', cloud: 'yes' },
    { feature: 'Voiceprint speaker identification', us: 'yes', coding: 'no', notetaker: 'partial', cloud: 'partial' },
    { feature: 'Meeting → agent, closed loop', us: 'yes', coding: 'no', notetaker: 'no', cloud: 'yes' },
    { feature: 'Per-agent permissions & skills', us: 'yes', coding: 'partial', notetaker: 'no', cloud: 'partial' },
    { feature: 'Buy once, own forever', us: 'yes', coding: 'partial', notetaker: 'no', cloud: 'no' },
  ];

  const Cell = ({ state }) => {
    if (state === 'yes') return <span className="cmp-mark cmp-yes" title="Yes">●</span>;
    if (state === 'partial') return <span className="cmp-mark cmp-partial" title="Partial">◐</span>;
    return <span className="cmp-mark cmp-no" title="No">○</span>;
  };

  return (
    <section className="compare" id="compare">
      <div className="section-container">
        <p className="overline overline--center">HOW IT COMPARES</p>
        <h2 className="section-title">Better than a notetaker. Friendlier than a raw agent.</h2>
        <p className="section-subtitle">
          Meeting tools can’t act on what they hear. Agent tools can’t hear at all—and most are a
          command line or one sprawling assistant. Your AI coworkers do both, organized like a team
          and safe to run.
        </p>

        <div className="cmp-table-wrap">
          <table className="cmp-table">
            <thead>
              <tr>
                <th className="cmp-feature-head" scope="col">
                  Capability
                </th>
                {columns.map((c) => (
                  <th key={c.key} scope="col" className={c.highlight ? 'cmp-col-us' : ''}>
                    <span className="cmp-col-label">{c.label}</span>
                    <span className="cmp-col-sub mono">{c.sub}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i}>
                  <th scope="row" className="cmp-feature-cell">
                    {r.feature}
                  </th>
                  {columns.map((c) => (
                    <td key={c.key} className={c.highlight ? 'cmp-col-us' : ''}>
                      <Cell state={r[c.key]} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="cmp-legend mono">
          <span><span className="cmp-mark cmp-yes">●</span> yes</span>
          <span><span className="cmp-mark cmp-partial">◐</span> partial</span>
          <span><span className="cmp-mark cmp-no">○</span> no</span>
        </div>

        <p className="cmp-footnote">
          Categories reflect publicly documented capabilities as of 2026 and are generalized across
          the named products. The point isn’t a new category—it’s doing what a notetaker and an
          agent manager each do, better and in one app you own.
        </p>
      </div>
    </section>
  );
};

export default Comparison;
